/**
 * THE TAB BAR — segmented, NOT routes.
 *
 * `filter.tab` drives it, so a tab is part of the view a link restores and a
 * saved view replays. Every visited tab stays MOUNTED behind `hidden` in the
 * shell, so scroll position, an open drill-down and chart hover state survive a
 * switch — and, because the data layer keys per endpoint, switching back
 * refetches nothing.
 *
 * Arrow keys move between tabs, which is what `role="tablist"` promises.
 */
import React, { useCallback, useRef } from 'react';
import { Tab, TABS, Tab as TabName } from '../state/filter-model';
import {
  Tab as TabButton,
  TabBar as Bar,
  TabIndicator,
  TabLabel,
} from '../dashboard-screen.styled';
import { useScrollEdges } from '../../../../scroll/scroll-affordance';
import { IndicatorGroup } from '../../../../../motion';

/** One pill for this tablist; a second tablist on the screen would name its own. */
const INDICATOR_ID = 'analytics-tabs';

const LABELS: Record<TabName, string> = {
  overview: 'Overview',
  trends: 'Trends',
  activity: 'Activity',
  records: 'Records',
  reports: 'Reports',
};

export interface TabBarProps {
  active: Tab;
  /** Trends is hidden without `analytics:read` — the same permission every
   *  /analytics/* route is gated on. A tab whose every request would 403 is
   *  worse than no tab. */
  hidden?: Tab[];
  onChange: (tab: Tab) => void;
}

export const TabBar: React.FC<TabBarProps> = ({ active, hidden = [], onChange }) => {
  const visible = TABS.filter(tab => !hidden.includes(tab));

  /* WP-33. Below md the tabs keep their intrinsic width and the strip scrolls,
     rather than five nowrap labels shrinking below their own text and painting
     over each other. That trade is only honest if the strip says it scrolls:
     the scrollbar is suppressed here, so the edge fade is the affordance, and
     it comes from the same hook WP-34's strips use. */
  const barRef = useRef<HTMLDivElement>(null);
  const { canScroll, atStart, atEnd } = useScrollEdges(barRef);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const index = visible.indexOf(active);
      if (index < 0) return;
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % visible.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + visible.length) % visible.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = visible.length - 1;
      else return;
      event.preventDefault();
      onChange(visible[next]);
    },
    [active, onChange, visible]
  );

  return (
    <Bar
      ref={barRef}
      role="tablist"
      aria-label="Dashboard sections"
      data-testid="tab-bar"
      $fadeStart={canScroll && !atStart}
      $fadeEnd={canScroll && !atEnd}
      /* Read by the live probe and by the unit test: the mask lives in an
         emotion class, and jsdom's getComputedStyle does not resolve those. */
      data-fade-start={canScroll && !atStart}
      data-fade-end={canScroll && !atEnd}
      onKeyDown={onKeyDown}
    >
      {/* THE ACTIVE PILL MOVES rather than being redrawn on each tab: it is
          rendered inside the active tab only, and the shared layoutId carries
          it across. The label sits above it on its own layer. */}
      <IndicatorGroup id={INDICATOR_ID}>
        {visible.map(tab => (
          <TabButton
            key={tab}
            type="button"
            role="tab"
            id={`dash-tab-${tab}`}
            aria-selected={active === tab}
            aria-controls={`dash-panel-${tab}`}
            tabIndex={active === tab ? 0 : -1}
            active={active === tab}
            data-testid={`tab-${tab}`}
            onClick={() => onChange(tab)}
          >
            {active === tab ? <TabIndicator id={INDICATOR_ID} /> : null}
            <TabLabel>{LABELS[tab]}</TabLabel>
          </TabButton>
        ))}
      </IndicatorGroup>
    </Bar>
  );
};

export const TAB_LABELS = LABELS;
